






































































































































































































































































import { PropType } from 'vue';

import {
  EStreamingType,
  TStreamer,
  EAspectRatios,
  TStreamerThumbnailQueryParams,
  TStreamerImages
} from '@/api/schema';
import { toJpeg } from 'html-to-image';
import {
  ETemplateValues,
  getThumbnailConfig,
  IThumbnailConfig
} from '@/helpers/StreamerThumbnailConfigs';
import { dataURLToBlob, objectToBlob } from '@/helpers/blobHelper';
import {
  addStreamerImages,
  createStreamer,
  updateStreamerImages
} from '@/api/CopyStake/Streamers';
import { errorToastMessage } from '@/helpers/errorToastMessage';

const ThumbnailImageUploader = (): Promise<any> =>
  import('@/components/ThumbnailImageUploader.vue');
const ThumbnailStreamerPreview = (): Promise<any> =>
  import('@/components/ThumbnailStreamerPreview.vue');
const ImageUploader = (): Promise<any> =>
  import('@/components/ImageUploader.vue');

enum EKeyToFieldMap {
  LOBBY_DESKTOP = 'lobbyCustomImageDesktopBlob',
  LOBBY_MOBILE = 'lobbyCustomImageMobileBlob',
  PROMO_DESKTOP = 'promoCustomImageDesktopBlob',
  PROMO_MOBILE = 'promoCustomImageMobileBlob'
}

const TEMPLATES_LIST = [
  { value: ETemplateValues.TEMPLATE_1, text: 'Template 1' },
  { value: ETemplateValues.TEMPLATE_2, text: 'Template 2' },
  { value: ETemplateValues.TEMPLATE_3, text: 'Template 3' },
  { value: ETemplateValues.TEMPLATE_4, text: 'Template 4' }
];
const ASPECTS_LIST = [
  { value: EAspectRatios.FOUR_THREE, text: '4:3', image: '4_3.png' },
  { value: EAspectRatios.THREE_FOUR, text: '3:4', image: '3_4.png' },
  { value: EAspectRatios.ONE_ONE, text: '1:1', image: '1_1.png' },
  { value: EAspectRatios.TWO_THREE, text: '2:3', image: '2_3.png' },
  { value: EAspectRatios.SIXTEEN_NINE, text: '16:9', image: '16_9.png' }
];

export default {
  name: 'CopyStakeStreamerThumbnailsDialog',
  props: {
    value: {
      type: Boolean as PropType<boolean>,
      default: (): boolean => false
    },
    streamer: {
      type: Object as PropType<TStreamer>,
      default: null
    }
  },
  components: {
    ImageUploader,
    ThumbnailStreamerPreview,
    ThumbnailImageUploader
  },
  data(): any {
    return {
      EAspectRatios,
      EKeyToFieldMap,
      TEMPLATES_LIST,
      ASPECTS_LIST,

      thumbnailTemplate: ETemplateValues.TEMPLATE_1,
      promoDesktopAspect: EAspectRatios.FOUR_THREE,
      promoMobileAspect: EAspectRatios.ONE_ONE,

      croppedStreamerImage: null,
      lobbyCustomImageDesktopBlob: null,
      lobbyCustomImageDesktopUrl: null,
      lobbyCustomImageMobileBlob: null,
      lobbyCustomImageMobileUrl: null,
      promoCustomImageDesktopBlob: null,
      promoCustomImageDesktopUrl: null,
      promoCustomImageMobileBlob: null,
      promoCustomImageMobileUrl: null,

      customImgUploaderErrors: {
        lobbyDesktop: false,
        lobbyMobile: false,
        promoDesktop: false,
        promoMobile: false
      },

      processing: false
    };
  },
  watch: {
    value(val: boolean): void {
      if (val) {
        if (this.streamer) {
          this.setSavedStreamerData();
        }
      }
    }
  },
  computed: {
    isEdit(): boolean {
      return this.streamer?.id;
    },
    isDialogVisible: {
      get(): boolean {
        return this.value;
      },
      set(value: boolean): void {
        this.$emit('input', value);
      }
    },
    streamerNickname(): string {
      return this.streamer?.nickname || '';
    },
    isDisabledSubmitBtn(): boolean {
      const hasCroppedImg = !!this.croppedStreamerImage;
      const hasAllCustomImg =
        !!this.lobbyCustomImageDesktopBlob &&
        !!this.lobbyCustomImageMobileBlob &&
        !!this.promoCustomImageDesktopBlob &&
        !!this.promoCustomImageMobileBlob;
      const hasAnyCustomUploaderErrors = Object.values(
        this.customImgUploaderErrors
      ).some((error) => error);

      return (
        !(hasCroppedImg || hasAllCustomImg) || hasAnyCustomUploaderErrors
      );
    },
    lobbyDesktopThumbnailConfig(): IThumbnailConfig {
      return getThumbnailConfig(
        this.thumbnailTemplate,
        EAspectRatios.SIXTEEN_NINE
      );
    },
    lobbyMobileThumbnailConfig(): IThumbnailConfig {
      return getThumbnailConfig(
        this.thumbnailTemplate,
        EAspectRatios.THREE_FOUR
      );
    },
    promoDesktopThumbnailConfig(): IThumbnailConfig {
      return getThumbnailConfig(
        this.thumbnailTemplate,
        this.promoDesktopAspect
      );
    },
    promoMobileThumbnailConfig(): IThumbnailConfig {
      return getThumbnailConfig(
        this.thumbnailTemplate,
        this.promoMobileAspect
      );
    }
  },
  methods: {
    setSavedStreamerData(): void {
      if (
        !this.streamer?.images ||
        !Object.keys(this.streamer.images).length
      ) {
        return;
      }

      const images = this.parsedSavedImages(this.streamer.images);

      const lobbyDesktopConfig = images.STREAMER_LOBBY_DESKTOP;
      this.lobbyCustomImageDesktopUrl = lobbyDesktopConfig.url;

      const lobbyMobileConfig = images.STREAMER_LOBBY_MOBILE;
      this.lobbyCustomImageMobileUrl = lobbyMobileConfig.url;

      const promoDesktopConfig = images.STREAMER_PROMO_DESKTOP;
      this.promoCustomImageDesktopUrl = promoDesktopConfig.url;
      this.promoDesktopAspect = promoDesktopConfig.aspect;

      const promoMobileConfig = images.STREAMER_PROMO_MOBILE;
      this.promoCustomImageMobileUrl = promoMobileConfig.url;
      this.promoMobileAspect = promoMobileConfig.aspect;
    },
    parsedSavedImages(
      images: TStreamerImages
    ): Record<string, { aspect: string; url: string }> {
      return Object.entries(images).reduce((acc, [key, url]) => {
        const parts = key.split('_');

        if (parts.length < 3) {
          console.error(`Invalid key format: "${key}"`);
          return acc;
        }

        const type = parts.slice(0, -2).join('_');
        const aspect = parts.slice(-2).join('_');

        acc[type] = {
          aspect,
          url
        };

        return acc;
      }, {});
    },

    getPreviewTransformStyles(aspect: EAspectRatios): {
      [key: string]: string;
    } {
      const stylesMap = {
        [EAspectRatios.FOUR_THREE]: {
          transform: '0.195',
          top: '1px',
          left: '0'
        },
        [EAspectRatios.THREE_FOUR]: {
          transform: '0.15',
          top: '0',
          left: '32px'
        },
        [EAspectRatios.ONE_ONE]: {
          transform: '0.15',
          top: '0',
          left: '17px'
        },
        [EAspectRatios.TWO_THREE]: {
          transform: '0.15',
          top: '0',
          left: '37px'
        },
        [EAspectRatios.SIXTEEN_NINE]: {
          transform: '0.195',
          top: '15px',
          left: '0'
        }
      };

      return {
        transform: `scale(${stylesMap[aspect].transform})`,
        transformOrigin: 'top left',
        display: 'inline-block',
        'border-radius': '50px',
        overflow: 'hidden',
        position: 'absolute',
        left: stylesMap[aspect].left,
        top: stylesMap[aspect].top
      };
    },

    handlerOnUploadImage(image: File): void {
      this.croppedStreamerImage = image;
    },
    handlerOnUploadCustomImage(imageFile: File, key: EKeyToFieldMap): void {
      this[key] = imageFile || null;
    },

    handlerOnBack(): void {
      this.$emit('back', this.streamer);
      this.isDialogVisible = false;
    },
    async handlerOnSubmit(): Promise<void> {
      try {
        if (this.processing) return;
        this.processing = true;

        let streamer: TStreamer = this.streamer;

        if (!this.isEdit) {
          streamer = await this.createNewStreamer();
        }

        const imagesFormData = await this.getImagesFormData();

        const aspectParams: TStreamerThumbnailQueryParams = {
          promoDesktopRatio: this.promoDesktopAspect,
          promoMobileRatio: this.promoMobileAspect
        };

        this.streamer.images?.STREAMER_LOBBY_DESKTOP_SIXTEEN_NINE
          ? await updateStreamerImages(
              streamer.id,
              imagesFormData,
              aspectParams
            )
          : await addStreamerImages(
              streamer.id,
              imagesFormData,
              aspectParams
            );

        this.closeDialog();

        this.$toast.success(
          `Streamer "${
            streamer?.nickname || streamer?.playerId
          }" successfully created`
        );

        this.$emit('submit', {
          isEdit: this.isEdit,
          streamer: streamer
        });
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.processing = false;
      }
    },
    async handlerOnCreateLater(): Promise<void> {
      try {
        if (this.processing) return;
        this.processing = true;

        const streamer: TStreamer = await this.createNewStreamer();

        this.closeDialog();

        this.$toast.success(
          `Streamer "${
            streamer?.nickname || streamer?.playerId
          }" successfully created`
        );

        this.$emit('submit', {
          isEdit: this.isEdit,
          streamer: streamer
        });
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.processing = false;
      }
    },

    async createNewStreamer(): Promise<TStreamer> {
      const formData = new FormData();

      const commonStreamerParams = {
        operatorUserId: this.streamer.operatorUserId,
        nickname: this.streamer.nickname,
        type: this.streamer.type,
        language: this.streamer.language,
        accessCode:
          (this.streamer.accessCode &&
            this.streamer.accessCode.toUpperCase()) ||
          null
      };

      const streamerParamsByType =
        this.streamer.streamingType === EStreamingType.WHIP
          ? {
              streamingType: EStreamingType.WHIP,
              categories: this.streamer.categories || [],
              shortBio: this.streamer.shortBio || '',
              socialLinks: this.streamer.socialLinks || {}
            }
          : {
              streamingType: EStreamingType.WEB
            };

      formData.append(
        'streamer',
        objectToBlob({
          ...commonStreamerParams,
          ...streamerParamsByType
        })
      );

      return await createStreamer(formData);
    },
    async getImagesFormData(): Promise<FormData> {
      const imagesFormData = new FormData();

      const lobbyDesktopBlob = this.lobbyCustomImageDesktopBlob
        ? this.lobbyCustomImageDesktopBlob
        : await this.getImageFromPreview('lobbyDesktopPreviewRef');
      const lobbyMobileBlob = this.lobbyCustomImageMobileBlob
        ? this.lobbyCustomImageMobileBlob
        : await this.getImageFromPreview('lobbyMobilePreviewRef');
      const promoDesktopBlob = this.promoCustomImageDesktopBlob
        ? this.promoCustomImageDesktopBlob
        : await this.getImageFromPreview('promoDesktopPreviewRef');
      const promoMobileBlob = this.promoCustomImageMobileBlob
        ? this.promoCustomImageMobileBlob
        : await this.getImageFromPreview('promoMobilePreviewRef');

      imagesFormData.append('lobbyDesktopImage', lobbyDesktopBlob);
      imagesFormData.append('lobbyMobileImage', lobbyMobileBlob);
      imagesFormData.append('promoDesktopImage', promoDesktopBlob);
      imagesFormData.append('promoMobileImage', promoMobileBlob);

      return imagesFormData;
    },
    async getImageFromPreview(refKey: string): Promise<Blob> {
      const thumbnailConfigMap = {
        lobbyDesktopPreviewRef: this.lobbyDesktopThumbnailConfig,
        lobbyMobilePreviewRef: this.lobbyMobileThumbnailConfig,
        promoDesktopPreviewRef: this.promoDesktopThumbnailConfig,
        promoMobilePreviewRef: this.promoMobileThumbnailConfig
      };

      const imgRef = this.$refs[refKey];

      if (!imgRef) {
        console.error(`Reference ${refKey} not found`);
        return null;
      }

      const dataUrl = await toJpeg(imgRef, {
        cacheBust: true,
        pixelRatio: 1,
        width: thumbnailConfigMap[refKey].templateImageWidth,
        height: thumbnailConfigMap[refKey].templateImageHeight,
        style: {
          transform: 'none',
          borderRadius: '0',
          position: 'static'
        }
      });

      return await dataURLToBlob(dataUrl);
    },

    clearConfigCustomImageUploader(refKey: string): void {
      const imgUploaderRef = this.$refs[refKey];

      if (!imgUploaderRef) {
        console.error(`Image uploader ${refKey} not found`);
        return;
      }

      imgUploaderRef.clearConfiguration();
    },

    closeDialog(clearData: boolean = true): void {
      this.isDialogVisible = false;
      this.$emit('close', clearData);

      if (clearData) {
        this.resetState();
      }
    },
    resetState(): void {
      this.processing = false;

      this.thumbnailTemplate = ETemplateValues.TEMPLATE_1;
      this.promoDesktopAspect = EAspectRatios.FOUR_THREE;
      this.promoMobileAspect = EAspectRatios.ONE_ONE;

      this.$refs.thumbnailImageUploader?.clearConfiguration();

      this.croppedStreamerImage = null;
      this.lobbyCustomImageDesktopBlob = null;
      this.lobbyCustomImageDesktopUrl = null;
      this.lobbyCustomImageMobileBlob = null;
      this.lobbyCustomImageMobileUrl = null;
      this.promoCustomImageDesktopBlob = null;
      this.promoCustomImageDesktopUrl = null;
      this.promoCustomImageMobileBlob = null;
      this.promoCustomImageMobileUrl = null;

      this.customImgUploaderErrors.lobbyMobile = false;
      this.customImgUploaderErrors.lobbyDesktop = false;
      this.customImgUploaderErrors.promoDesktop = false;
      this.customImgUploaderErrors.promoMobile = false;
    }
  }
};
